import CampaignPlatformType from '@/Enums/CampaignManagement/CampaignPlatformType.enum';
import CampaignStatus from '@/Enums/CampaignManagement/CampaignStatus.enum';
import AudienceTargetingService from '@/Services/AudienceTargeting.service';
import { CAMPAIGN_MANAGEMENT_ROW_TYPE, DEFAULT_SORT_MODEL } from '@/constants/campaignManagement';
import {
  AUDIENCES_BY_CHANNELS_ENDPOINT,
  CAMPAIGNS_ENDPOINT,
  CAMPAIGNS_LIST_ENDPOINT,
  CHANNELS_ENDPOINT,
  DV360_TICKETS_INSERTION_ORDERS_ENDPOINT,
  DV360_TICKETS_LINE_ITEMS_ENDPOINT,
  LINE_ITEMS_ENDPOINT,
  PRODUCTS_BY_CHANNELS_ENDPOINT,
} from '@/constants/endpoints';
import { LEVEL_VALUES } from '@/constants/pacing';
import { beeswaxAdvertiserFilter } from '@/helpers/advertisers/advertiserFilters.helpers';
import { campaignManagementAdvertiserMapper } from '@/helpers/advertisers/advertiserMappers.helpers';
import { fetchClients } from '@/helpers/axios';
import {
  getCampaignManagementFilters,
  getOptionsFromCampaignsList,
  getProcessedCampaignsReport,
} from '@/helpers/campaignManagement';
import { firstPartyBeeswaxSegmentsByAdvertiser } from '@/helpers/campaignManagement/firstPartySegments';
import { formatClientsResponseData } from '@/helpers/clients';
import { getDefaultDateRangeFormatted } from '@/helpers/date/dateRange.helpers';
import { selectIsDV360CampaignManagementEnabled } from '@/store/selectors/currentWhitelabel.selectors';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '../asyncThunkHandlers';

const firstPartySegmentsUrl = '/campaigns/segments';

const normalizeProductsByChannels = data => {
  if (Array.isArray(data)) {
    return data.reduce((acc, item) => {
      const channelName = item?.channelName ?? item?.channel?.name;
      if (!channelName) return acc;
      return {
        ...acc,
        [channelName]: {
          ...item,
          products: item?.products ?? [],
        },
      };
    }, {});
  }

  if (!data || typeof data !== 'object') {
    return {};
  }

  return Object.entries(data).reduce((acc, [channelName, value]) => {
    const channelData = value?.products ? value : { products: value ?? [] };
    return {
      ...acc,
      [channelName]: {
        ...channelData,
        products: channelData.products ?? [],
      },
    };
  }, {});
};

const DEFAULT_FILTERS = {
  page: 1,
  limit: 15,
  search: '',
  sort: DEFAULT_SORT_MODEL,
  level: LEVEL_VALUES.FULL_VIEW,
  pacingStatus: [],
  status: [],
  clients: [],
  advertisers: [],
  campaigns: [],
  channelTypes: [],
  ...getDefaultDateRangeFormatted(7),
  isArchived: false,
  platform: CampaignPlatformType.Omnichannel,
};

const initialState = {
  filters: DEFAULT_FILTERS,
  clients: null,
  campaigns: [],
  products: null,
  productsRequestId: null,
  channels: null,
  channelsRequestId: null,
  audiences: null,
  audiencesRequestId: null,
  beeswaxFirstPartySegments: {},
  beeswaxFirstPartySegmentsRequestId: null,
  totalCampaigns: 0,
  clientsRequestId: true,
  campaignsRequestId: false,
  campaignsData: [],
  campaignsDataRequestId: true,
  campaignsDataError: null,
  originalCampaignsData: [],
  activeCampaign: null,
  activeCampaignRequestId: null,
  activeCampaignError: null,
  createLineItemRequestId: null,
  updateLineItemRequestId: null,
  geoData: [],
  geoDataRequestId: null,
  applyRowsExpandAfterUpdate: false,
};

export const campaignManagementSlice = createSlice({
  name: 'campaignManagement',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload
        ? {
            ...state.filters,
            ...action.payload,
          }
        : DEFAULT_FILTERS;
    },
    resetFilters: state => {
      state.filters = DEFAULT_FILTERS;
    },
    // A saved view (#5128) replaces every filter it carries; anything it doesn't
    // carry goes back to default. Page size and sort stay as they are; the page
    // goes back to 1 since the new filters can return fewer rows.
    applySavedView: (state, action) => {
      state.filters = {
        ...DEFAULT_FILTERS,
        ...action.payload,
        page: 1,
        limit: state.filters.limit,
        sort: state.filters.sort,
      };
    },
    updateFilteredCampaignsData: state => {
      const campaignsData = getProcessedCampaignsReport(state.originalCampaignsData, state.filters);
      state.campaignsData = campaignsData;
    },
    resetActiveCampaign: state => {
      state.activeCampaign = null;
    },
    resetRowsExpandAfterUpdate: state => {
      state.applyRowsExpandAfterUpdate = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getCampaignsData.pending, pendingCaseHandler('campaignsData'))
      .addCase(getCampaignsData.rejected, rejectedCaseHandler('campaignsData'))
      .addCase(getCampaignsData.fulfilled, (state, action) => {
        if (state.campaignsDataRequestId === action.meta.requestId) {
          Object.entries(action.payload).forEach(([key, value]) => {
            state[key] = value;
          });
          state.campaignsDataRequestId = null;
        }
      })
      .addCase(partialUpdateCampaign.fulfilled, (state, action) => {
        const updatedCampaign = action.payload;
        state.originalCampaignsData = state.originalCampaignsData.map(campaign =>
          campaign.id === updatedCampaign.id ? updatedCampaign : campaign,
        );
        state.campaignsData = state.campaignsData.map(item =>
          item.campaignId === updatedCampaign.id && item.type === 'campaign'
            ? {
                ...item,
                status: updatedCampaign.status,
                endDate: updatedCampaign.end_date,
                booked: updatedCampaign.impressions_budget,
              }
            : item,
        );
        state.applyRowsExpandAfterUpdate = true;
      })
      .addCase(partialUpdateLineItem.fulfilled, (state, action) => {
        const updatedLineItem = action.payload;
        state.originalCampaignsData = state.originalCampaignsData.map(campaign =>
          campaign.id === updatedLineItem.campaign_id
            ? {
                ...campaign,
                lineItems: campaign.lineItems.map(li => (li.id === updatedLineItem.id ? updatedLineItem : li)),
              }
            : campaign,
        );
        state.campaignsData = state.campaignsData.map(item =>
          item.lineItemId === updatedLineItem.id && item.type === 'lineItem'
            ? {
                ...item,
                status: updatedLineItem.status,
                endDate: updatedLineItem.end_date,
                booked: updatedLineItem.impressions_budget,
              }
            : item,
        );
        state.applyRowsExpandAfterUpdate = true;
      })
      .addCase(createLineItem.pending, pendingCaseHandler('createLineItem'))
      .addCase(createLineItem.rejected, rejectedCaseHandler('createLineItem'))
      .addCase(createLineItem.fulfilled, (state, action) => {
        if (state.createLineItemRequestId === action.meta.requestId) {
          state.createLineItemRequestId = null;
          state.activeCampaign.lineItems = [
            ...(state.activeCampaign?.lineItems || []),
            action.payload,
            ...(action.payload?.duplicates || []),
          ];
        }
      })
      .addCase(updateLineItem.pending, pendingCaseHandler('updateLineItem'))
      .addCase(updateLineItem.rejected, rejectedCaseHandler('updateLineItem'))
      .addCase(updateLineItem.fulfilled, (state, action) => {
        if (state.updateLineItemRequestId === action.meta.requestId) {
          state.updateLineItemRequestId = null;
          state.activeCampaign.lineItems = [
            ...state.activeCampaign.lineItems.map(lineItem =>
              lineItem.id === action.payload.id ? action.payload : lineItem,
            ),
            ...(action.payload?.duplicates || []),
          ];
        }
      })
      .addCase(uploadLineItemDomainLists.fulfilled, (state, action) => {
        const { lineItemId } = action.payload;
        if (state.activeCampaign?.lineItems) {
          state.activeCampaign.lineItems = state.activeCampaign.lineItems.map(li =>
            li.id === lineItemId ? { ...li, has_custom_domain_lists: true } : li,
          );
        }
      })
      .addCase(getClients.pending, pendingCaseHandler('clients'))
      .addCase(getClients.rejected, rejectedCaseHandler('clients'))
      .addCase(getClients.fulfilled, fulfilledCaseHandler('clients'))
      .addCase(getCampaigns.pending, pendingCaseHandler('campaigns'))
      .addCase(getCampaigns.rejected, rejectedCaseHandler('campaigns'))
      .addCase(getCampaigns.fulfilled, fulfilledCaseHandler('campaigns'))
      .addCase(getCampaignData.pending, pendingCaseHandler('activeCampaign'))
      .addCase(getCampaignData.rejected, rejectedCaseHandler('activeCampaign'))
      .addCase(getCampaignData.fulfilled, fulfilledCaseHandler('activeCampaign'))
      .addCase(createCampaign.pending, pendingCaseHandler('activeCampaign'))
      .addCase(createCampaign.rejected, rejectedCaseHandler('activeCampaign'))
      .addCase(createCampaign.fulfilled, fulfilledCaseHandler('activeCampaign'))
      .addCase(updateCampaign.pending, pendingCaseHandler('activeCampaign'))
      .addCase(updateCampaign.rejected, rejectedCaseHandler('activeCampaign'))
      .addCase(updateCampaign.fulfilled, fulfilledCaseHandler('activeCampaign'))
      .addCase(getProducts.pending, pendingCaseHandler('products'))
      .addCase(getProducts.rejected, rejectedCaseHandler('products'))
      .addCase(getProducts.fulfilled, fulfilledCaseHandler('products'))
      .addCase(getAudiences.pending, pendingCaseHandler('audiences'))
      .addCase(getAudiences.rejected, rejectedCaseHandler('audiences'))
      .addCase(getAudiences.fulfilled, fulfilledCaseHandler('audiences'))
      .addCase(getChannels.pending, pendingCaseHandler('channels'))
      .addCase(getChannels.rejected, rejectedCaseHandler('channels'))
      .addCase(getChannels.fulfilled, fulfilledCaseHandler('channels'))
      .addCase(getBeeswaxFirstPartySegments.pending, pendingCaseHandler('beeswaxFirstPartySegments'))
      .addCase(getBeeswaxFirstPartySegments.rejected, rejectedCaseHandler('beeswaxFirstPartySegments'))
      .addCase(getBeeswaxFirstPartySegments.fulfilled, fulfilledCaseHandler('beeswaxFirstPartySegments'))
      .addCase(approveActiveCampaign.fulfilled, state => {
        state.activeCampaign.status = CampaignStatus.ACTIVE;
      })
      .addCase(approveCampaign.fulfilled, (state, action) => {
        state.originalCampaignsData = state.originalCampaignsData.map(campaign =>
          campaign.id === action.payload ? { ...campaign, status: CampaignStatus.ACTIVE } : campaign,
        );
        state.campaignsData = state.campaignsData.map(campaign =>
          campaign.campaignId === action.payload ? { ...campaign, status: CampaignStatus.ACTIVE } : campaign,
        );
      })
      .addCase(archiveCampaign.fulfilled, (state, action) => {
        const hasCampaign = state.originalCampaignsData?.some(c => c.id === action.payload);
        state.originalCampaignsData = state.originalCampaignsData?.filter(c => c.id !== action.payload) || [];
        state.campaignsData = state.campaignsData?.filter(row => row.campaignId !== action.payload) || [];

        if (hasCampaign) {
          state.totalCampaigns = Math.max(0, (state.totalCampaigns || 0) - 1);
        }
        state.applyRowsExpandAfterUpdate = true;
      })
      .addCase(archiveLineItem.fulfilled, (state, action) => {
        const archivedLineItemId = action.payload;

        state.originalCampaignsData = state.originalCampaignsData.map(campaign => {
          const hasLineItem = campaign.lineItems?.some(lineItem => lineItem.id === archivedLineItemId);
          if (!hasLineItem) return campaign;

          return {
            ...campaign,
            lineItems: campaign.lineItems.filter(li => li.id !== archivedLineItemId),
          };
        });

        state.campaignsData =
          state.campaignsData.filter(row => {
            if (row.type === CAMPAIGN_MANAGEMENT_ROW_TYPE.LINE_ITEM) {
              return row.lineItemId !== archivedLineItemId;
            }
            return true;
          }) || [];

        state.applyRowsExpandAfterUpdate = true;
      })
      .addCase(partialUpdateDV360InsertionOrder.fulfilled, (state, action) => {
        const { campaignId, status, endDate } = action.meta.arg;
        state.campaignsData = state.campaignsData.map(item =>
          item.campaignId === campaignId && item.type === CAMPAIGN_MANAGEMENT_ROW_TYPE.CAMPAIGN
            ? {
                ...item,
                ...(status !== undefined ? { status } : {}),
                ...(endDate !== undefined ? { endDate } : {}),
              }
            : item,
        );
        state.applyRowsExpandAfterUpdate = true;
      })
      .addCase(partialUpdateDV360LineItem.fulfilled, (state, action) => {
        const { lineItemId, status, endDate } = action.meta.arg;
        state.campaignsData = state.campaignsData.map(item =>
          item.lineItemId === lineItemId && item.type === CAMPAIGN_MANAGEMENT_ROW_TYPE.LINE_ITEM
            ? {
                ...item,
                ...(status !== undefined ? { status } : {}),
                ...(endDate !== undefined ? { endDate } : {}),
              }
            : item,
        );
        state.applyRowsExpandAfterUpdate = true;
      })
      .addCase(archiveDV360InsertionOrder.fulfilled, (state, action) => {
        const archivedCampaignId = action.payload;
        const hasCampaign = state.campaignsData?.some(
          row => row.campaignId === archivedCampaignId && row.type === CAMPAIGN_MANAGEMENT_ROW_TYPE.CAMPAIGN,
        );
        state.campaignsData = state.campaignsData?.filter(row => row.campaignId !== archivedCampaignId) || [];

        if (hasCampaign) {
          state.totalCampaigns = Math.max(0, (state.totalCampaigns || 0) - 1);
        }
        state.applyRowsExpandAfterUpdate = true;
      })
      .addCase(archiveDV360LineItem.fulfilled, (state, action) => {
        const archivedLineItemId = action.payload;
        state.campaignsData =
          state.campaignsData.filter(row => {
            if (row.type === CAMPAIGN_MANAGEMENT_ROW_TYPE.LINE_ITEM) {
              return row.lineItemId !== archivedLineItemId;
            }
            return true;
          }) || [];
        state.applyRowsExpandAfterUpdate = true;
      });
  },
});

export const {
  setFilters,
  resetFilters,
  applySavedView,
  updateFilteredCampaignsData,
  resetRowsExpandAfterUpdate,
  resetActiveCampaign,
} = campaignManagementSlice.actions;

export const getClients = createAsyncThunk(
  'campaignManagementSlice/getClients',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await fetchClients(null, payload?.signal);
      return formatClientsResponseData(response, campaignManagementAdvertiserMapper, beeswaxAdvertiserFilter);
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getCampaigns = createAsyncThunk(
  'campaignManagementSlice/getCampaigns',
  async (payload, { rejectWithValue }) => {
    try {
      const { advertisers, clients } = payload;
      const advertiserIds = [];

      if (!advertisers?.length) {
        clients.forEach(client => {
          client.advertisers.forEach(advertiser => advertiserIds.push(advertiser.value));
        });
      } else {
        advertisers.forEach(advertiser => {
          advertiserIds.push(advertiser.value);
        });
      }

      if (!advertiserIds?.length) {
        return [];
      }

      const { data } = await axios.get(CAMPAIGNS_LIST_ENDPOINT, {
        params: {
          advertisers: advertiserIds,
        },
      });

      return getOptionsFromCampaignsList(data);
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getCampaignsData = createAsyncThunk(
  'campaignManagementSlice/getCampaignsData',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      let {
        campaignManagement: { filters, clients },
        me: { role },
      } = state;

      const { platform, ...otherFilters } = filters;

      const isDV360CampaignManagementEnabled = selectIsDV360CampaignManagementEnabled(state);

      const url =
        isDV360CampaignManagementEnabled && platform === CampaignPlatformType.Google
          ? DV360_TICKETS_INSERTION_ORDERS_ENDPOINT
          : CAMPAIGNS_ENDPOINT;

      const appliedFilter = getCampaignManagementFilters(otherFilters, role, clients);

      const {
        data: {
          data: campaignsData,
          meta: { total: totalCampaigns },
        },
      } = await axios.get(url, { params: appliedFilter }, payload);

      filters = getState()?.campaignManagement?.filters;

      return {
        originalCampaignsData: campaignsData,
        campaignsData: getProcessedCampaignsReport(campaignsData, filters),
        totalCampaigns,
      };
    } catch (err) {
      return rejectWithValue(err?.response?.status);
    }
  },
);

export const partialUpdateCampaign = createAsyncThunk(
  'campaignManagementSlice/partialUpdateCampaign',
  async (payload, { rejectWithValue, getState }) => {
    try {
      const { campaignId, status, endDate, booked } = payload;
      const { campaignsData } = getState().campaignManagement;

      if (!campaignsData.find(camp => camp.campaignId === campaignId)) {
        throw 'Failed to find Campaign to update';
      }

      const updateData = {};
      if (status !== undefined) updateData.status = status;
      if (endDate !== undefined) updateData.endDate = endDate;
      if (booked !== undefined) updateData.impressionsBudget = Number(booked);

      const { data } = await axios.put(`${CAMPAIGNS_ENDPOINT}/${campaignId}`, updateData);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const partialUpdateLineItem = createAsyncThunk(
  'campaignManagementSlice/partialUpdateLineItem',
  async (payload, { rejectWithValue, getState }) => {
    try {
      const { lineItemId, status, endDate, booked } = payload;
      const { campaignsData } = getState().campaignManagement;
      const lineItem = campaignsData.find(li => li.lineItemId === lineItemId);

      if (!lineItem) {
        throw 'Failed to find Line Item to update';
      }

      const updateData = { campaignId: lineItem.campaignId, productId: lineItem.productId };
      if (status !== undefined) updateData.status = status;
      if (endDate !== undefined) updateData.endDate = endDate;
      if (booked !== undefined) updateData.impressionsBudget = Number(booked);

      const { data } = await axios.put(`${LINE_ITEMS_ENDPOINT}/${lineItemId}`, updateData);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getCampaignData = createAsyncThunk(
  'campaignManagementSlice/getCampaignData',
  async (payload, { rejectWithValue }) => {
    const { campaignId, ...params } = payload;
    try {
      const { data } = await axios.get(`${CAMPAIGNS_ENDPOINT}/${campaignId}`, {
        params,
      });
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.status);
    }
  },
);

export const createCampaign = createAsyncThunk(
  'campaignManagementSlice/createCampaign',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(CAMPAIGNS_ENDPOINT, payload);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const updateCampaign = createAsyncThunk(
  'campaignManagementSlice/updateCampaign',
  async (payload, { rejectWithValue }) => {
    try {
      const { id, ...restPayload } = payload;
      const { data } = await axios.put(`${CAMPAIGNS_ENDPOINT}/${id}`, restPayload);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getProducts = createAsyncThunk(
  'campaignManagementSlice/getProducts',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(PRODUCTS_BY_CHANNELS_ENDPOINT, {
        params: payload,
      });
      return normalizeProductsByChannels(data);
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getAudiences = createAsyncThunk(
  'campaignManagementSlice/getAudiences',
  async (_payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(AUDIENCES_BY_CHANNELS_ENDPOINT);
      return AudienceTargetingService.mapAudiencesByChannelToControlOptions(data ?? []);
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getChannels = createAsyncThunk(
  'campaignManagementSlice/getChannels',
  async (_payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(CHANNELS_ENDPOINT);
      return data ?? [];
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
  {
    condition(_, { getState }) {
      const {
        campaignManagement: { channels },
      } = getState();
      return !channels?.length;
    },
  },
);

export const getBeeswaxFirstPartySegments = createAsyncThunk(
  'campaignManagementSlice/getBeeswaxFirstPartySegments',
  async () => {
    const { data } = await axios.get(firstPartySegmentsUrl);
    return firstPartyBeeswaxSegmentsByAdvertiser(data);
  },
  {
    condition(_, { getState }) {
      const {
        campaignManagement: { beeswaxFirstPartySegments },
      } = getState();

      return !Object.keys(beeswaxFirstPartySegments ?? {})?.length;
    },
  },
);

export const createLineItem = createAsyncThunk(
  'campaignManagementSlice/createLineItem',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(LINE_ITEMS_ENDPOINT, payload);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const updateLineItem = createAsyncThunk(
  'campaignManagementSlice/updateLineItem',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${LINE_ITEMS_ENDPOINT}/${payload.id}`, payload);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const uploadLineItemDomainLists = createAsyncThunk(
  'campaignManagementSlice/uploadLineItemDomainLists',
  async ({ lineItemId, allowDomains, blockDomains }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${LINE_ITEMS_ENDPOINT}/${lineItemId}/domain-lists`, {
        allowDomains,
        blockDomains,
      });
      return { ...data, lineItemId };
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const fetchLineItemDomainLists = createAsyncThunk(
  'campaignManagementSlice/fetchLineItemDomainLists',
  async (lineItemId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${LINE_ITEMS_ENDPOINT}/${lineItemId}/domain-lists`);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// activeCampaign (the bulk fetch LineItemForm reads from) never carries this — the backend
// only computes it on a single-item GET to avoid one Beeswax call per line item on every
// campaign load (flank#4301). So a dedicated on-demand fetch, not a field already in state.
export const fetchLineItemHasUnmanagedBeeswaxBiddingSettings = createAsyncThunk(
  'campaignManagementSlice/fetchLineItemHasUnmanagedBeeswaxBiddingSettings',
  async (lineItemId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${LINE_ITEMS_ENDPOINT}/${lineItemId}/unmanaged-beeswax-bidding-settings`);
      return data.hasUnmanagedBeeswaxBiddingSettings ?? false;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const approveActiveCampaign = createAsyncThunk(
  'campaignManagementSlice/approveActiveCampaign',
  async (_, { getState }) => {
    const { activeCampaign } = getState().campaignManagement;
    if (!activeCampaign) {
      throw new Error('No active campaign found');
    }
    await axios.post(`${CAMPAIGNS_ENDPOINT}/${activeCampaign.id}/approve`);
  },
);

export const approveCampaign = createAsyncThunk('campaignManagementSlice/approveCampaign', async ({ campaignId }) => {
  await axios.post(`${CAMPAIGNS_ENDPOINT}/${campaignId}/approve`);
  return campaignId;
});

export const archiveCampaign = createAsyncThunk(
  'campaignManagementSlice/archiveCampaign',
  async ({ campaignId, isArchived }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${CAMPAIGNS_ENDPOINT}/${campaignId}/archive`, { isArchived });

      return data.id;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const archiveLineItem = createAsyncThunk(
  'campaignManagementSlice/archiveLineItem',
  async ({ lineItemId, isArchived }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${LINE_ITEMS_ENDPOINT}/${lineItemId}/archive`, { isArchived });

      return data.id;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const partialUpdateDV360InsertionOrder = createAsyncThunk(
  'campaignManagementSlice/partialUpdateDV360InsertionOrder',
  async (payload, { rejectWithValue }) => {
    try {
      const { campaignId, status, endDate } = payload;
      const updateData = {};
      if (status !== undefined) updateData.status = status;
      if (endDate !== undefined) updateData.endDate = endDate;

      const { data } = await axios.put(`${DV360_TICKETS_INSERTION_ORDERS_ENDPOINT}/${campaignId}`, updateData);
      return { id: campaignId, ...data };
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const partialUpdateDV360LineItem = createAsyncThunk(
  'campaignManagementSlice/partialUpdateDV360LineItem',
  async (payload, { rejectWithValue }) => {
    try {
      const { lineItemId, status, endDate } = payload;
      const updateData = {};
      if (status !== undefined) updateData.status = status;
      if (endDate !== undefined) updateData.endDate = endDate;

      const { data } = await axios.put(`${DV360_TICKETS_LINE_ITEMS_ENDPOINT}/${lineItemId}`, updateData);
      return { id: lineItemId, ...data };
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const archiveDV360InsertionOrder = createAsyncThunk(
  'campaignManagementSlice/archiveDV360InsertionOrder',
  async ({ campaignId, isArchived }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${DV360_TICKETS_INSERTION_ORDERS_ENDPOINT}/${campaignId}/archive`, {
        isArchived,
      });
      return data?.id ?? campaignId;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const archiveDV360LineItem = createAsyncThunk(
  'campaignManagementSlice/archiveDV360LineItem',
  async ({ lineItemId, isArchived }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${DV360_TICKETS_LINE_ITEMS_ENDPOINT}/${lineItemId}/archive`, { isArchived });
      return data?.id ?? lineItemId;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export default campaignManagementSlice.reducer;
